import { z } from "zod";
import { apiService } from "@/services/api.service";
import {
	type AuditLogDisplay,
	AuditLogDisplaySchema,
	type CreateStatusLifecycleInfo,
	CreateStatusLifecycleInfoSchema,
	type EditStatusLifecycleDetailsInfo,
	EditStatusLifecycleDetailsInfoSchema,
	type EditStatusLifecycleInfo,
	EditStatusLifecycleInfoSchema,
	type JobStatusLifecyclesQueryParams,
	LifecycleApiError,
	normalizeStatusLifecyclesListParams,
	type ServiceResult,
	ServiceResultSchema,
	type StatusCategoryTypeDisplay,
	StatusCategoryTypeDisplaySchema,
	type StatusLifecycleDisplay,
	StatusLifecycleDisplayArraySchema,
	StatusLifecycleDisplaySchema,
	type StatusLifecyclesListParams,
	type StatusLifecyclesQueryInput,
	type StatusLifecyclesQueryParams,
	type TicketStatusLifecyclesQueryParams,
} from "./lifecycle.types";

const AuditLogArray = z.array(AuditLogDisplaySchema);

function buildScopedJobOrTicketLifecycleParams(
	params?: JobStatusLifecyclesQueryParams | TicketStatusLifecyclesQueryParams,
): Record<string, string> | undefined {
	if (!params) return undefined;
	const out: Record<string, string> = {};
	if (params.subOrganizationId)
		out.subOrganizationId = params.subOrganizationId;
	if (params.jobTypeId) out.jobTypeId = params.jobTypeId;
	return Object.keys(out).length > 0 ? out : undefined;
}

async function parseStatusLifecycleArrayResponse(
	response: unknown,
): Promise<StatusLifecycleDisplay[]> {
	try {
		return StatusLifecycleDisplayArraySchema.parse(response);
	} catch (error) {
		if (error instanceof z.ZodError) {
			throw new LifecycleApiError(
				`Invalid status lifecycles response: ${error.message}`,
				error,
			);
		}
		throw error;
	}
}

export class LifecycleService {
	/**
	 * GET /workflows/status-lifecycles/{start}/{count}
	 */
	async getStatusLifecyclesPage(
		start: number,
		count: number,
		params?: StatusLifecyclesQueryInput,
	): Promise<StatusLifecycleDisplay[]> {
		try {
			const query = normalizeStatusLifecyclesListParams(params);
			const response = await apiService.get<unknown>(
				`/workflows/status-lifecycles/${start}/${count}`,
				{ params: query },
			);
			return parseStatusLifecycleArrayResponse(response);
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			throw new LifecycleApiError(
				`Failed to get status lifecycles page: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /workflows/status-lifecycles/count
	 */
	async getStatusLifecyclesCount(
		params?: StatusLifecyclesQueryInput,
	): Promise<number> {
		try {
			const query = normalizeStatusLifecyclesListParams(params);
			const response = await apiService.get<unknown>(
				"/workflows/status-lifecycles/count",
				{ params: query },
			);
			return z.coerce.number().parse(response);
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			throw new LifecycleApiError(
				`Failed to get status lifecycles count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /workflows/status-lifecycles/{start}/{count}/export — CSV
	 */
	async exportStatusLifecyclesCsv(
		start: number,
		count: number,
		params?: StatusLifecyclesQueryInput,
		columns?: string | null,
	): Promise<Blob> {
		try {
			const base = normalizeStatusLifecyclesListParams(params);
			const query: StatusLifecyclesListParams & { columns?: string } = {
				...base,
				...(columns != null && columns !== "" ? { columns } : {}),
			};
			return await apiService.get<Blob>(
				`/workflows/status-lifecycles/${start}/${count}/export`,
				{
					params: query,
					headers: { Accept: "text/csv" },
					responseType: "blob",
				},
			);
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			throw new LifecycleApiError(
				`Failed to export status lifecycles: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /workflows/status-lifecycles/{id}
	 */
	async getStatusLifecycleById(
		id: string,
		options?: { organizationId?: string },
	): Promise<StatusLifecycleDisplay> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.get<unknown>(
				`/workflows/status-lifecycles/${encodeURIComponent(id)}`,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			return StatusLifecycleDisplaySchema.parse(response);
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			if (error instanceof z.ZodError) {
				throw new LifecycleApiError(
					`Invalid status lifecycle detail: ${error.message}`,
					error,
				);
			}
			throw new LifecycleApiError(
				`Failed to get status lifecycle ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * Legacy full list — uses count + paginated list (replaces deprecated GET /workflows/status-lifecycles).
	 */
	async getStatusLifecycles(
		params?: StatusLifecyclesQueryParams,
	): Promise<StatusLifecycleDisplay[]> {
		const normalized = normalizeStatusLifecyclesListParams({
			subOrganizationId: params?.suborganizationId,
			entityTypeClass: params?.entityTypeClass,
			jobTypeId: params?.jobTypeId,
			ticketTypeId: params?.ticketTypeId,
			assetTypeId: params?.assetTypeId,
		});
		const total = await this.getStatusLifecyclesCount(normalized);
		if (total <= 0) return [];
		const cap = Math.min(Number(total), 10_000);
		return this.getStatusLifecyclesPage(1, cap, normalized);
	}

	/**
	 * GET /workflows/entity-type-classes
	 */
	async getStatusCategoryTypes(): Promise<StatusCategoryTypeDisplay[]> {
		try {
			const response = await apiService.get<unknown>(
				"/workflows/entity-type-classes",
			);
			return z.array(StatusCategoryTypeDisplaySchema).parse(response);
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			throw new LifecycleApiError(
				`Failed to get status category types: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /jobs/status-lifecycles
	 */
	async getJobStatusLifecycles(
		params?: JobStatusLifecyclesQueryParams,
	): Promise<StatusLifecycleDisplay[]> {
		try {
			const query = buildScopedJobOrTicketLifecycleParams(params);
			const response = await apiService.get<unknown>(
				"/jobs/status-lifecycles",
				query ? { params: query } : undefined,
			);
			return parseStatusLifecycleArrayResponse(response);
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			throw new LifecycleApiError(
				`Failed to get job status lifecycles: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /tickets/status-lifecycles
	 */
	async getTicketStatusLifecycles(
		params?: TicketStatusLifecyclesQueryParams,
	): Promise<StatusLifecycleDisplay[]> {
		try {
			const query = buildScopedJobOrTicketLifecycleParams(params);
			const response = await apiService.get<unknown>(
				"/tickets/status-lifecycles",
				query ? { params: query } : undefined,
			);
			return parseStatusLifecycleArrayResponse(response);
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			throw new LifecycleApiError(
				`Failed to get ticket status lifecycles: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * POST /workflows/status-lifecycles
	 */
	async createStatusLifecycle(
		data: CreateStatusLifecycleInfo,
		errorPrefix = "Failed to create lifecycle",
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const body = CreateStatusLifecycleInfoSchema.parse(data);
			const organizationId = options?.organizationId;

			const response = await apiService.post<ServiceResult>(
				"/workflows/status-lifecycles",
				body,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw LifecycleApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}

			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new LifecycleApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}

			throw new LifecycleApiError(
				`${errorPrefix}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /workflows/status-lifecycles/{id}
	 */
	async updateStatusLifecycle(
		id: string,
		data: EditStatusLifecycleInfo,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const validated = EditStatusLifecycleInfoSchema.parse(data);
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/workflows/status-lifecycles/${encodeURIComponent(id)}`,
				validated,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw LifecycleApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new LifecycleApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}
			throw new LifecycleApiError(
				`Failed to update lifecycle ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /workflows/status-lifecycle-details/{id}
	 * Updates only name and description. Requires `EditStatusLifecycleDetails`.
	 */
	async updateStatusLifecycleDetails(
		id: string,
		data: EditStatusLifecycleDetailsInfo,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const validated = EditStatusLifecycleDetailsInfoSchema.parse(data);
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/workflows/status-lifecycle-details/${encodeURIComponent(id)}`,
				validated,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw LifecycleApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			if (error instanceof z.ZodError) {
				const fieldErrors = error.issues.map((err) => ({
					field: err.path.join("."),
					message: err.message,
				}));
				throw new LifecycleApiError(
					`Validation failed: ${fieldErrors.map((e) => `${e.field}: ${e.message}`).join(", ")}`,
					error,
				);
			}
			throw new LifecycleApiError(
				`Failed to update lifecycle details ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	// ==================== LIFECYCLE HISTORY ====================

	/**
	 * GET /workflows/{id}/history/count
	 */
	async getWorkflowHistoryCount(
		lifecycleId: string,
		options?: { organizationId?: string },
	): Promise<number> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.get<number>(
				`/workflows/${encodeURIComponent(lifecycleId)}/history/count`,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			return z.number().parse(response);
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			throw new LifecycleApiError(
				`Failed to get lifecycle history count: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * GET /workflows/status-lifecycles/{id}/history/{start}/{count}
	 */
	async getStatusLifecycleHistory(
		lifecycleId: string,
		start: number = 1,
		count: number = 10,
		options?: { organizationId?: string },
	): Promise<AuditLogDisplay[]> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.get<AuditLogDisplay[]>(
				`/workflows/status-lifecycles/${encodeURIComponent(lifecycleId)}/history/${start}/${count}`,
				{
					headers:
						organizationId !== undefined
							? { "organization-id": organizationId }
							: undefined,
				},
			);
			return AuditLogArray.parse(response);
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			throw new LifecycleApiError(
				`Failed to get lifecycle history: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * PUT /workflows/status-lifecycles/{id}/activate
	 * Promotes a lifecycle from Draft (active=false) to Active (active=true). One-way.
	 */
	async activateStatusLifecycle(
		id: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.put<ServiceResult>(
				`/workflows/status-lifecycles/${encodeURIComponent(id)}/activate`,
				undefined,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw LifecycleApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			throw new LifecycleApiError(
				`Failed to activate lifecycle ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	/**
	 * DELETE /workflows/status-lifecycles/{id}
	 * NOTE: endpoint not yet in swagger — placeholder ready for when backend adds it
	 */
	async deleteStatusLifecycle(
		id: string,
		options?: { organizationId?: string },
	): Promise<ServiceResult> {
		try {
			const organizationId = options?.organizationId;
			const response = await apiService.delete<ServiceResult>(
				`/workflows/status-lifecycles/${encodeURIComponent(id)}`,
				{
					headers:
						organizationId && organizationId.length > 0
							? { "organization-id": organizationId }
							: undefined,
				},
			);

			const result = ServiceResultSchema.parse(response);

			if (!result.success) {
				throw LifecycleApiError.fromServiceResult(result);
			}

			return result;
		} catch (error) {
			if (error instanceof LifecycleApiError) {
				throw error;
			}
			throw new LifecycleApiError(
				`Failed to delete lifecycle ${id}: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}
}

export const lifecycleService = new LifecycleService();
